import * as z from "zod";
import type { SupplyIntelConfig } from "./config";

const SHARE_OF_LIST = z.number().min(0.15).max(0.9);

/**
 * A brand's own resale prices, as shares of its store price, from market data
 * such as eBay sold listings. Brands without one use the config's prices, as
 * does whichever share a brand leaves out.
 */
export const SupplyIntelBrandPricingSchema = z
  .strictObject({
    /** Where the prices come from, e.g. "eBay sold, last 90 days". */
    source: z.string().min(1),
    /** When the source was read. */
    sourceDate: z.iso.date(),
    salePriceShareOfList: SHARE_OF_LIST.optional(),
    preOwnedSalePriceShareOfList: SHARE_OF_LIST.optional(),
  })
  .refine(
    (pricing) =>
      pricing.salePriceShareOfList !== undefined ||
      pricing.preOwnedSalePriceShareOfList !== undefined,
    { message: "Needs at least one price share" },
  );
export type SupplyIntelBrandPricing = z.infer<
  typeof SupplyIntelBrandPricingSchema
>;

/** The config with a brand's own resale prices in place of the defaults. */
export function supplyIntelConfigForBrand(
  config: SupplyIntelConfig,
  pricing: SupplyIntelBrandPricing | null | undefined,
): SupplyIntelConfig {
  if (!pricing) {
    return config;
  }
  return {
    ...config,
    estimate: {
      ...config.estimate,
      salePriceShareOfList:
        pricing.salePriceShareOfList ?? config.estimate.salePriceShareOfList,
      preOwnedSalePriceShareOfList:
        pricing.preOwnedSalePriceShareOfList ??
        config.estimate.preOwnedSalePriceShareOfList,
    },
  };
}

/** Which of the brand's prices are its own and where they come from, e.g. "own new price, from …". */
export function supplyIntelBrandPricingLabel(
  pricing: SupplyIntelBrandPricing,
): string {
  const source = `from ${pricing.source} as of ${pricing.sourceDate}`;
  if (pricing.salePriceShareOfList === undefined) {
    return `own pre-owned price, ${source}; new at the default`;
  }
  if (pricing.preOwnedSalePriceShareOfList === undefined) {
    return `own new price, ${source}; pre-owned at the default`;
  }
  return `own new and pre-owned prices, ${source}`;
}
